import { get } from 'lodash-es';
import { createSelector } from 'reselect';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import { BANNER_HEIGHT } from 'src/constants/navigation';
import { FeatureKeys } from 'src/features';
import { GlobalState } from 'src/redux/global/reducer';
import {
  shouldDisplayFlag,
  shouldDisplayIncidentBanner,
} from 'src/sections/global/components/flags/site-message/site-message-store';
import { getGlobal } from 'src/selectors/state-slicing-selectors';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { SiteMessage } from 'src/types/site-message';
import { BucketState } from 'src/types/state';

export const getIsSearchDrawerOpen = createSelector(
  getGlobal,
  (state: GlobalState) => state.isSearchDrawerOpen
);

export const getIsMobileHeaderActive = createSelector(
  getGlobal,
  (state: GlobalState) => state.isMobileHeaderActive
);

export const getUpToSmallBreakpointState = createSelector(
  getGlobal,
  (state: GlobalState) => state.isUpToSmallBreakpointActive
);

export const getIsOrgAdmin = createSelector(
  getGlobal,
  (state: GlobalState) => !!state.isOrgAdmin
);

export const getWorkspaceOrgId = createSelector(
  getGlobal,
  (state: GlobalState) => state.workspaceOrgId
);

export const getCcpEntitlementId = createSelector(
  getGlobal,
  (state: GlobalState) => state.ccp?.ccpEntitlementId
);

export const getCcpTransactionAccountId = createSelector(
  getGlobal,
  (state: GlobalState) => state.ccp?.ccpTransactionAccountId
);

export const getCanViewEntitlement = createSelector(
  getGlobal,
  (state: GlobalState) => state.ccp?.canViewEntitlement
);

export const getIsWorkspaceSumEnabled = createSelector(
  getGlobal,
  (state: GlobalState) => !!state.isWorkspaceSumEnabled
);

export const getIsWorkspaceAdmin = createSelector(
  getGlobal,
  (state: GlobalState) => !!state.isWorkspaceAdmin
);

export const getisUnlinkedWsFlagNotificationRelevant = createSelector(
  getGlobal,
  (state: GlobalState) => !!state.isUnlinkedWsFlagNotificationRelevant
);

export const getCurrentUserLocale = createSelector(
  getGlobal,
  (state: GlobalState) => {
    const { locale } = state;
    if (!locale) {
      return undefined;
    }
    return locale
      .split('-')
      .map((part, index) => (index === 1 ? part.toUpperCase() : part))
      .join('-');
  }
);

export const siteMessagesForAppearance = (
  state: BucketState,
  targetAppearance: 'flag' | 'banner'
) => {
  const siteMessage = get(state, 'global.siteMessage');
  const currentUser = getCurrentUser(state);

  if (!siteMessage || !currentUser) {
    return null;
  }

  if (
    !getIsMobileHeaderActive(state) &&
    siteMessage.appearance === targetAppearance &&
    // Banners cannot be dismissed, so we only need to check the "should display" status for Flags.
    (siteMessage.appearance === 'banner' ||
      shouldDisplayFlag(currentUser.uuid, siteMessage.id))
  ) {
    return siteMessage;
  } else {
    return null;
  }
};

export const getStatusPageBanner = (state: BucketState) => {
  return get(state, 'global.siteMessageStatuspageIncident', null);
};

export const getIsWorkspaceBootstrapping = (state: BucketState) => {
  return get(state, 'global.workspaceBootstrapping', false);
};

// this needs to be defined outside of the getSiteMessageBanner selector so a new
// object is not created every time it's called
const bootstrappingMessage: SiteMessage = {
  id: 0,
  title: 'Bootstrap in progress',
  appearance: 'banner',
  text: 'Workspace setup in progress. Groups and users may not be fully available for multiple hours. This banner will disappear once the process completes, upon page refresh.',
  isIncident: false,
  linkType: 'refresh',
};

// Note: If there's a context workspace and it's having its permissions bootstrapping is in progress,
// we will show that banner first. If not, and there is an incident, the statuspage banner will be shown.
// Finally if neither of the above are true, the highest priority site message will be shown.
export const getSiteMessageBanner = (state: BucketState) => {
  const isWorkspaceBootstrapping = getIsWorkspaceBootstrapping(state);
  if (isWorkspaceBootstrapping) return bootstrappingMessage;

  const statuspageIncident = getStatusPageBanner(state);
  const currentUser = getCurrentUser(state);
  if (!currentUser && statuspageIncident) return statuspageIncident;
  const shouldDisplayIncident =
    statuspageIncident &&
    shouldDisplayIncidentBanner(currentUser!.uuid, statuspageIncident.id);
  if (shouldDisplayIncident) {
    return statuspageIncident;
  }

  return siteMessagesForAppearance(state, 'banner');
};

export const getIsSiteMessageActive = (state: BucketState) =>
  !!getSiteMessageBanner(state);

export const getSiteMessageFlag = (state: BucketState) =>
  siteMessagesForAppearance(state, 'flag');

// ideally the nav height would not need to be known
export function getNav4TopNavHeight() {
  // when this gate is enabled + FHS is enabled the nav4 top height is 56px
  // FHS is fully rolled out in BBC, so not checking that explicitly here
  if (FeatureGates.checkGate('platform_dst_nav4_top_nav_increase_height')) {
    return 56;
  }
  return 48;
}

// In a follow up PR I will update the all the instances of getCombinedBannerAndHorizontalNavHeight to not pass the
// isBeingRenderedInsideMobileNav argument (COREX-13070)
export const getCombinedBannerAndHorizontalNavHeight = (
  state: BucketState,
  // this will be removed in a future PR (COREX-13070)
  _isBeingRenderedInsideMobileNav: boolean,
  consentBannerHeight: number
) => {
  const bannerHeight = getSiteMessageBanner(state)
    ? BANNER_HEIGHT + consentBannerHeight
    : consentBannerHeight;
  const horizontalNavHeight = getNav4TopNavHeight();
  return bannerHeight + horizontalNavHeight;
};

export const getIsPageVisible = createSelector(
  getGlobal,
  (state: GlobalState) => state.isPageVisible
);

export const getIsOffline = createSelector(
  getGlobal,
  (state: GlobalState) => state.isOffline
);

export const getIsWebSocketOpen = createSelector(
  getGlobal,
  (state: GlobalState) => state.isWebSocketOpen
);

export const getRepositoryPageLoadingStatus = createSelector(
  getGlobal,
  (state: GlobalState) => state.repositoryPageLoadingStatus
);

export const getCurrentUserEmail = createSelector(
  getGlobal,
  (state: GlobalState) => state.currentUserEmail
);

export const getIsFeedbackModalOpen = createSelector(
  getGlobal,
  (state: GlobalState) => state.isFeedbackModalOpen
);

export const getUserGlobalTheme = createSelector(
  getGlobal,
  (state: GlobalState) => state.globalTheme
);

export const getIsCustomMergeChecksEnabled = createSelector(
  getGlobal,
  (state: GlobalState) => {
    if (state.isCustomMergeChecksEnabled !== undefined) {
      // If user has toggled checks, return the state they toggled to
      return state.isCustomMergeChecksEnabled;
    } else {
      // Otherwise return the global state from core
      return state.features[FeatureKeys.customMergeChecksActivated];
    }
  }
);

export const getDefaultWorkspace = createSelector(
  getGlobal,
  (state: GlobalState) => state.defaultWorkspace
);

export const getJiraConnectionsCreatedAt = createSelector(
  getGlobal,
  (state: GlobalState) => state.jiraConnectionsCreatedAt
);
